import {
  Avatar,
  AvatarFallbackText,
  AvatarImage,
} from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import { Heading } from "@/components/ui/heading";
import { VStack } from "@/components/ui/vstack";
import { ParticipantDTO } from "@/generated/api/models";
import { getName } from "@/utils/name";
import { HStack } from "@/components/ui/hstack";
import { Icon } from "@/components/ui/icon";
import { ChevronRight } from "lucide-react-native";
import { useMemo, useState } from "react";
import { Badge, BadgeText } from "@/components/ui/badge";
import { Input, InputField, InputIcon, InputSlot } from "@/components/ui/input";
import { Keyboard } from "react-native";
import { getI18n } from "react-i18next";
import { centsToValue, valueToCents } from "@/utils/currency.precision";
import { parseCurrencyInput } from "@/utils/currency.parser";

export type RestaurantWalletTransferItemProps = {
  participant: ParticipantDTO;
  value: number;
  valueString: string;
  currencyCode: string;
  currencySymbol: string;
  precision: number;
  readonly: boolean;
  onChange: (newValue: { value: number; valueString: string }) => void;
};

export function RestaurantWalletTransferItem({
  participant,
  value,
  valueString,
  currencySymbol,
  precision,
  readonly,
  onChange,
}: RestaurantWalletTransferItemProps) {
  const name = useMemo(() => getName(participant.user), [participant.user]);

  const uniqueTags = useMemo(
    () => [...new Set([...participant.publicTags, ...participant.privateTags])],
    [participant.privateTags, participant.publicTags]
  );

  const handleKeyPress = () => {
    Keyboard.dismiss();
  };

  const [inputValue, setInputValue] = useState({
    value,
    valueString,
  });

  const locale = getI18n().language;

  const formatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: "decimal",
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }),
    [precision]
  );

  return (
    <Card
      size="sm"
      variant="filled"
      className="flex-row justify-between w-full items-center p-2 md:p-4"
    >
      <HStack className="gap-2 md:gap-4 items-center">
        <Avatar size="md" className="border-2 border-outline-0">
          <AvatarFallbackText>{name}</AvatarFallbackText>
          <AvatarImage
            source={{ uri: participant.user.avatarImage ?? undefined }}
          />
        </Avatar>
        <VStack className="gap-0">
          <Heading
            size="sm"
            className="text-ellipsis overflow-hidden whitespace-nowrap"
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {name}
          </Heading>
          {participant.user.email && (
            <Text size="xs">{participant.user.email}</Text>
          )}
          {uniqueTags.length > 0 && (
            <HStack className="gap-2 mt-2">
              {uniqueTags.map((t) => (
                <Badge key={t} action="info">
                  <BadgeText>{t}</BadgeText>
                </Badge>
              ))}
            </HStack>
          )}
        </VStack>
      </HStack>

      <Input
        size="sm"
        className="w-2/6 max-w-44 min-w-24"
        isDisabled={readonly}
      >
        <InputSlot className="pl-1 md:pl-3">
          <Text>{currencySymbol}</Text>
        </InputSlot>
        <InputField
          className="w-full"
          placeholder="Enter amount"
          value={inputValue.valueString}
          onChangeText={(t) => {
            const newValue =
              parseCurrencyInput(t, precision) ?? inputValue.value;
            setInputValue({ valueString: t, value: newValue });
          }}
          onBlur={() => {
            const newInputValue = {
              value: inputValue.value,
              valueString: formatter.format(
                centsToValue(inputValue.value, precision)
              ),
            };
            setInputValue(newInputValue);
            onChange(newInputValue);
          }}
          onSubmitEditing={handleKeyPress}
          inputMode="decimal"
          returnKeyType="done"
        />
      </Input>
    </Card>
  );
}
